import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { getCanonicalFeatureTitle } from "@/utils/accessControl";
import { getPlanDisplayFeatures } from "@/utils/subscriptionFeatureDisplay";
import type { NormalizedPlanFeature } from "./utils";

interface TenantPlanFeatureBadgesProps {
  features: readonly NormalizedPlanFeature[];
  enableCoreFeatures?: boolean;
  planTitle?: string | null;
}

function isRapidPlanTitle(planTitle?: string | null): boolean {
  return planTitle?.trim().toLowerCase() === "rapid";
}

export const TenantPlanFeatureBadges = ({
  features,
  enableCoreFeatures,
  planTitle,
}: TenantPlanFeatureBadgesProps): React.JSX.Element => {
  const { t } = useTranslation();
  const displayFeatures = getPlanDisplayFeatures(
    features,
    enableCoreFeatures === true && !isRapidPlanTitle(planTitle),
  );

  if (displayFeatures.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("subscriptions.tenantManagement.planDetails.noFeatures")}
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {displayFeatures.map((feature, index) => {
        if ("titleKey" in feature) {
          return (
            <Badge key={`${feature.key}-${index}`} variant="new_secondary">
              {t(feature.titleKey)}
            </Badge>
          );
        }

        const title = getCanonicalFeatureTitle(feature.key, feature.title);
        const value =
          feature.type === "NUMBER" && feature.value
            ? ` (${feature.value})`
            : "";

        return (
          <Badge key={`${feature.key}-${index}`} variant="new_default">
            {title}
            {value}
          </Badge>
        );
      })}
    </div>
  );
};
